import NetworkUtil from "./NetworkUtil";
import {Sample} from "../types/Sample";
import {ServerResponse} from "../types/ServerResponse";
import {ReportDownloadHistory, ReportDownloadSummary, SampleDocumentType} from "../types/ReportDownload";

export default class SamplesAPI {

    public static async getFilteredSamples(page, filter):Promise<{num_pages:number, totalSamples:number, table_data?:Sample[]}>{
        return NetworkUtil.makePost("/api/admin/sampleSearch?page=" + page, filter);
    }

    public static async getFilteredSamplesForCheckin(page, filter):Promise<{num_pages:number, totalSamples:number, table_data?:Sample[]}>{
        return NetworkUtil.makePost("/api/admin/sampleSearchForCheckin?page=" + page, filter);
    }

    public static async getAllSamplesForAdminHome():Promise<{facilitySamplesArray}>{
        return NetworkUtil.makeGet("/api/admin/getAllSamplesForAdminHome")
    }

    public static async getTodaySamples():Promise<{num_pages:number, totalSamples:number, table_data?:Sample[]}>{
        return NetworkUtil.makeGet("/api/admin/getTodaySamples")
    }

    public static async resultRetrievalForSample(reqNum):Promise<any>{
        return NetworkUtil.makePost("/api/admin/resultRetrievalForSample" , {reqNum: reqNum})
    }

    public static async updateSample(sample, insuranceInformation, insuranceCards, submissionFiles?):Promise<ServerResponse>{
        let data = new FormData()
        data.set('insuranceInformation', JSON.stringify(insuranceInformation))
        data.set('patientInformation', JSON.stringify(sample))
        if(insuranceCards){
            for(let i = 0; i < insuranceCards.length; i++){
                let ext = insuranceCards[i].type.split('/').pop();
                data.append('insuranceCards', insuranceCards[i], `${i === 0 ? 'front' : 'back'}.${ext}`)
            }
        }
        if(submissionFiles){
            for(let i = 0; i < submissionFiles.length; i++){
                data.append('submissionFiles', submissionFiles[i])
            }
        }
        return NetworkUtil.postForm("/api/admin/sampleEdit", data);
    }

    public static async updateResultsAccessedDate(id):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/sampleResultsAccessedDate", {ID: id});
    }

    public static editSample(editBody:SampleCheckinBody):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/sample/checkin", editBody);
    }

    public static getInsuranceCardImagesFromCloud(data):Promise<any>{
        return NetworkUtil.makePostForBlob("/api/admin/getInsuranceCardImagesFromCloud", data);
    }

    public static downloadFilesFromCloud(data):Promise<any>{
        return NetworkUtil.makePostForBlob("/api/admin/downloadFilesFromCloud", data);
    }

    public static getSubmittedFilesFromCloud(reqNum):Promise<{success: boolean, reason?:string,  data?}>{
        return NetworkUtil.makePost("/api/admin/getSubmittedFilesFromCloud", {ReqNum: reqNum});
    }

    public static async resultsReviewedSendPatientEmail(id):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/resultsReviewedSendPatientEmail", {ID: id});
    }

    public static async generateExcelDocBasedOnService(serviceID, facilityID):Promise<any>{
        return NetworkUtil.makePostForBlob("/api/admin/generateExcelDocBasedOnService", {serviceID: serviceID, facilityID: facilityID});
    }

    public static upgradeSample(sample, paymentInfo):Promise<{success:boolean,reason:string,data:[]}>{
      return NetworkUtil.makePost("/api/admin/sample/upgradeSample", {sample:sample, paymentInfo: paymentInfo});
    }

    public static async cancelSample(sample):Promise<ServerResponse>{
        return NetworkUtil.makePost("/api/admin/cancelSample", {sample: sample});
    }

    public static downloadPDFs(reqNums, PDFTypeToDownload):Promise<Blob | ServerResponse>{
        // PDFTypeToDownload is "RequisitionForm"; result reports go through downloadSampleDocuments
        return NetworkUtil.makePostForBuffer("/api/admin/pdfReport/downloadMultipleSamplePDFs", {records: reqNums, PDFTypeToDownload: PDFTypeToDownload});
    }

    // Selected documents for one or more samples merged into a single PDF.
    // skippedDocuments lists "<ReqNum>:<SampleDocumentType>" entries that weren't available,
    // from the response header on success or the error body when none were.
    public static async downloadSampleDocuments(reqNums:string[], documentTypes:SampleDocumentType[]):Promise<{blob?: Blob, error?: ServerResponse, skippedDocuments: string[]}>{
        const response = await NetworkUtil.makePostForBufferWithHeaders("/api/admin/pdfReport/downloadSampleDocuments", {reqNums: reqNums, documentTypes: documentTypes});
        const skippedHeader = response.headers?.get('X-Skipped-Documents');
        const skippedDocuments = skippedHeader
            ? skippedHeader.split(',').map(d => d.trim()).filter(d => d)
            : (response.error as any)?.skippedDocuments || [];
        return { blob: response.blob, error: response.error, skippedDocuments: skippedDocuments };
    }

    // Download counts + last download date per sample/report type, for the Samples table.
    public static getReportDownloadSummary(reqNums:string[]):Promise<{success:boolean,reason:string,data?:ReportDownloadSummary[]}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/getReportDownloadSummary", {reqNums: reqNums});
    }

    // Who downloaded a sample's reports and when, newest first.
    public static getReportDownloadHistory(reqNum:string):Promise<{success:boolean,reason:string,data?:ReportDownloadHistory[]}>{
        return NetworkUtil.makePost("/api/admin/pdfReport/getReportDownloadHistory", {reqNum: reqNum});
    }

}

export interface SampleCheckinBody{
    ID?
    PatientFirstName:string
    PatientLastName:string,
    PatientDOB
    ServiceID
    TestIDs
    SpecimenSourceID
    SpecimenID?
    Lot?
    HasLabTest?
    SpecimenIDsArray?
    LotsArray?
}